import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle, faTimesCircle, faInfoCircle, faCircle,
  faChevronDown, faChevronRight, faSearch, faFilter,
  faSync, faFileExport, faBug, faLink
} from '@fortawesome/free-solid-svg-icons';
import axios from '../../config/axiosConfig';
import owaspWstgService from '../../services/owaspWstgService';

const STATUS_CONFIG = {
  pass: { icon: faCheckCircle, color: 'text-green-400', bg: 'bg-green-900/20', label: 'Pass' },
  fail: { icon: faTimesCircle, color: 'text-red-400', bg: 'bg-red-900/20', label: 'Fail' },
  info: { icon: faInfoCircle, color: 'text-blue-400', bg: 'bg-blue-900/20', label: 'Info' },
  not_tested: { icon: faCircle, color: 'text-gray-500', bg: '', label: 'Not Tested' }
};

const WstgChecklist = ({ webTarget, isDarkMode }) => {
  const [coverage, setCoverage] = useState(null);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [expandedCats, setExpandedCats] = useState(new Set());
  const [expandedTest, setExpandedTest] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [testDetails, setTestDetails] = useState({});

  useEffect(() => {
    if (webTarget?._id) loadCoverage();
  }, [webTarget]);

  const loadCoverage = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/web-reports/wstg/${webTarget._id}`);
      setCoverage(res.data.coverage || []);
      setCategories(res.data.categories || []);
      setSummary(res.data.summary || {});
    } catch (err) {
      console.error('Failed to load WSTG coverage:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (testId, status) => {
    setUpdating(testId);
    try {
      await axios.put(`/web-reports/wstg/${webTarget._id}/test/${testId}`, { status });
      loadCoverage();
    } catch (err) {
      console.error('Failed to update test status:', err);
    } finally {
      setUpdating(null);
    }
  };

  const loadTestDetail = async (testId) => {
    if (testDetails[testId]) return;
    try {
      const data = await owaspWstgService.getByTestId(testId);
      setTestDetails(prev => ({ ...prev, [testId]: data }));
    } catch {}
  };

  const toggleCategory = (code) => {
    setExpandedCats(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const toggleTest = (testId) => {
    if (expandedTest === testId) {
      setExpandedTest(null);
    } else {
      setExpandedTest(testId);
      loadTestDetail(testId);
    }
  };

  const exportCSV = () => {
    if (!coverage) return;
    const rows = [['Test ID', 'Category', 'Test Name', 'Status', 'Source', 'Findings', 'Notes']];
    for (const c of coverage) {
      rows.push([c.testId, c.categoryCode, c.testName, c.status, c.source || '', c.findingCount, c.notes]);
    }
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wstg-coverage-${webTarget.baseUrl.replace(/[^a-z0-9]/gi, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Group coverage by category
  const grouped = {};
  if (coverage) {
    for (const c of coverage) {
      if (!grouped[c.categoryCode]) grouped[c.categoryCode] = [];
      grouped[c.categoryCode].push(c);
    }
  }

  // Filter
  const matchesFilter = (c) => {
    if (filter !== 'all' && c.status !== filter) return false;
    if (search && !c.testId.toLowerCase().includes(search.toLowerCase()) && !c.testName.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  };

  const bg = isDarkMode ? 'bg-gray-900' : 'bg-white';
  const text = isDarkMode ? 'text-gray-300' : 'text-gray-700';
  const border = isDarkMode ? 'border-gray-700' : 'border-gray-200';

  if (loading) {
    return <div className={`flex items-center justify-center h-64 ${text}`}><FontAwesomeIcon icon={faSync} spin className="mr-2" /> Loading WSTG coverage...</div>;
  }

  return (
    <div className={`h-full flex flex-col ${bg}`}>
      {/* Summary bar */}
      {summary && (
        <div className={`px-4 py-3 border-b ${border} flex items-center gap-4 flex-wrap`}>
          <div className={`text-xs font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            OWASP WSTG Coverage: {summary.coveragePercent}%
          </div>
          <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden max-w-xs">
            <div className="h-full bg-teal-500 rounded-full" style={{ width: `${summary.coveragePercent}%` }} />
          </div>
          <div className="flex gap-3 text-[10px]">
            <span className="text-green-400">{summary.pass} pass</span>
            <span className="text-red-400">{summary.fail} fail</span>
            <span className="text-blue-400">{summary.info} info</span>
            <span className="text-gray-500">{summary.notTested} untested</span>
          </div>
          <button onClick={exportCSV} className={`px-2 py-1 text-[10px] rounded ${isDarkMode ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            <FontAwesomeIcon icon={faFileExport} className="mr-1" /> Export CSV
          </button>
        </div>
      )}

      {/* Filter bar */}
      <div className={`px-4 py-2 border-b ${border} flex items-center gap-3`}>
        <div className="relative flex-1 max-w-xs">
          <FontAwesomeIcon icon={faSearch} className={`absolute left-2 top-1/2 -translate-y-1/2 text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search tests..."
            className={`w-full pl-7 pr-2 py-1 text-xs rounded border ${isDarkMode ? 'bg-gray-800 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
          />
        </div>
        <div className="flex gap-1">
          {['all', 'not_tested', 'fail', 'pass', 'info'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-2 py-1 text-[10px] rounded ${filter === f ? 'bg-teal-600 text-white' : (isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600')}`}>
              {f === 'all' ? 'All' : STATUS_CONFIG[f]?.label || f}
            </button>
          ))}
        </div>
        <button onClick={loadCoverage} className={`p-1 text-xs ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>
          <FontAwesomeIcon icon={faSync} />
        </button>
      </div>

      {/* Categories accordion */}
      <div className="flex-1 overflow-y-auto">
        {categories.map(cat => {
          const tests = (grouped[cat.code] || []).filter(matchesFilter);
          if (tests.length === 0 && filter !== 'all') return null;
          const isExpanded = expandedCats.has(cat.code);
          const catConfig = owaspWstgService.getCategoryConfig(cat.code);
          const pct = cat.total > 0 ? Math.round((cat.tested / cat.total) * 100) : 0;

          return (
            <div key={cat.code} className={`border-b ${border}`}>
              {/* Category header */}
              <button
                onClick={() => toggleCategory(cat.code)}
                className={`w-full px-4 py-2.5 flex items-center gap-3 text-left hover:bg-gray-800/30`}
              >
                <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                <span className={`text-xs font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  {cat.code}
                </span>
                <span className={`text-xs flex-1 ${text}`}>{catConfig.name}</span>
                <div className="flex items-center gap-2">
                  {cat.fail > 0 && <span className="text-[10px] text-red-400">{cat.fail} fail</span>}
                  {cat.pass > 0 && <span className="text-[10px] text-green-400">{cat.pass} pass</span>}
                  <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{cat.tested}/{cat.total}</span>
                  <div className="w-16 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-teal-500 rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              </button>

              {/* Tests */}
              {isExpanded && tests.map(test => {
                const sc = STATUS_CONFIG[test.status];
                const isTestExpanded = expandedTest === test.testId;
                const detail = testDetails[test.testId];

                return (
                  <div key={test.testId} className={`${sc.bg}`}>
                    <div
                      onClick={() => toggleTest(test.testId)}
                      className={`px-4 py-2 pl-10 flex items-center gap-3 cursor-pointer hover:bg-gray-800/20`}
                    >
                      <FontAwesomeIcon icon={sc.icon} className={`text-xs ${sc.color}`} />
                      <span className={`text-[11px] font-mono ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{test.testId}</span>
                      <span className={`text-xs flex-1 ${text}`}>{test.testName}</span>
                      {test.findingCount > 0 && (
                        <span className="text-[10px] text-red-400 flex items-center gap-1">
                          <FontAwesomeIcon icon={faBug} /> {test.findingCount}
                        </span>
                      )}
                      {test.source && (
                        <span className={`text-[9px] px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>
                          {test.source}
                        </span>
                      )}
                      {/* Quick status buttons */}
                      <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                        {['pass', 'fail', 'info', 'not_tested'].map(s => (
                          <button key={s} onClick={() => handleStatusUpdate(test.testId, s)}
                            disabled={updating === test.testId}
                            className={`w-5 h-5 rounded text-[8px] ${test.status === s ? 'ring-1 ring-teal-400' : ''} ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
                            title={STATUS_CONFIG[s].label}>
                            <FontAwesomeIcon icon={STATUS_CONFIG[s].icon} className={STATUS_CONFIG[s].color} />
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Expanded test detail */}
                    {isTestExpanded && (
                      <div className={`px-4 py-3 pl-14 space-y-2 border-t ${border}`}>
                        {test.notes && (
                          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            <strong>Notes:</strong> {test.notes}
                          </div>
                        )}
                        {detail?.summary && (
                          <div className={`text-xs ${text}`}>
                            <strong>Summary:</strong> {detail.summary}
                          </div>
                        )}
                        {detail?.objectives?.length > 0 && (
                          <div className={`text-xs ${text}`}>
                            <strong>Objectives:</strong>
                            <ul className="list-disc list-inside mt-1">
                              {detail.objectives.map((o, i) => <li key={i}>{o}</li>)}
                            </ul>
                          </div>
                        )}
                        {detail?.howToTest && (
                          <div className={`text-xs ${text}`}>
                            <strong>How to Test:</strong>
                            <div className="mt-1 whitespace-pre-wrap opacity-80">{detail.howToTest}</div>
                          </div>
                        )}
                        {detail?.remediation && (
                          <div className={`text-xs ${text}`}>
                            <strong>Remediation:</strong> {detail.remediation}
                          </div>
                        )}
                        {test.mitreMapping?.length > 0 && (
                          <div className={`text-xs ${text}`}>
                            <strong>MITRE ATT&CK:</strong>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {test.mitreMapping.map((m, i) => (
                                <span key={i} className={`px-1.5 py-0.5 rounded text-[9px] ${isDarkMode ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                                  {m.techniqueId} - {m.techniqueName}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WstgChecklist;
